/**
 * In-tab Lark error-discovery session.
 *
 * Opens only after a server-issued system error id exists.
 * Interview system string is `GET /client-errors/qa-prompt` → `prompt`
 * (`LARK_ERROR_QA_PROMPT`). Cab-log voice is not this drawer.
 * Never opens a PR, never writes product data.
 */

import { classifyBlockedPrompt } from '@/lib/lark-error-origin';
import { buildErrorFactsTurn } from '@/lib/lark-error-facts';
import { newLarkErrorSessionId } from '@/lib/lark-error-id';
import { redactSecrets } from '@/lib/lark-error-redact';
import {
  fetchClientErrorQaPrompt,
  postClientErrorEvent,
  postClientErrorQa,
  type ClientErrorQaPrompt,
} from '@/lib/lark-error-reporter';
import type {
  LarkErrorDiagnosis,
  LarkErrorEvent,
  LarkErrorEventKind,
  LarkErrorPacket,
} from '@/lib/lark-error-types';
import { LARK_ERROR_QUESTIONS } from '@/lib/lark-error-types';

export interface LarkErrorSessionState {
  open: boolean;
  ended: boolean;
  /** True once the BE accepted the Q&A answers — drives the sheet's acknowledgement. */
  submitted: boolean;
  offTopicRedirects: number;
  packet: LarkErrorPacket | null;
  qaPrompt: ClientErrorQaPrompt | null;
}

const listeners = new Set<(state: LarkErrorSessionState) => void>();

let state: LarkErrorSessionState = {
  open: false,
  ended: false,
  submitted: false,
  offTopicRedirects: 0,
  packet: null,
  qaPrompt: null,
};

function emit(): void {
  listeners.forEach((listener) => listener(state));
}

function now(): string {
  return new Date().toISOString();
}


/**
 * Every append replaces the top-level state object. React listeners
 * receive the state by reference (`setSession(state)`); mutating
 * `state.packet` in place kept the same reference, so `useState`
 * bailed out and Send appeared to do nothing.
 */
function appendEvent(kind: LarkErrorEventKind, payload: Record<string, unknown>): LarkErrorEvent {
  const event: LarkErrorEvent = { at: now(), kind, payload };
  const packet = state.packet;
  if (packet) {
    state = {
      ...state,
      packet: {
        ...packet,
        events: [...packet.events, event],
      },
    };
    void postClientErrorEvent(packet.systemErrorId, event);
  }
  return event;
}

function appendTranscript(
  role: 'lark' | 'user',
  text: string,
  extra?: { blocked?: boolean; blockedReason?: LarkErrorPacket['transcript'][number]['blockedReason'] },
): void {
  if (!state.packet) return;
  state = {
    ...state,
    packet: {
      ...state.packet,
      transcript: [
        ...state.packet.transcript,
        { role, text: redactSecrets(text), at: now(), ...extra },
      ],
    },
  };
}

export function getLarkErrorSession(): LarkErrorSessionState {
  return state;
}

export function subscribeLarkErrorSession(
  listener: (next: LarkErrorSessionState) => void,
): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Interview starts only with a server-issued system error id.
 * No id → no drawer. Brain prompt is fetched from the backend.
 */
export function openLarkErrorInterview(
  systemErrorId: string,
  diagnosis: LarkErrorDiagnosis,
  sessionId = newLarkErrorSessionId(),
  qaPrompt: ClientErrorQaPrompt | null = null,
): boolean {
  const id = systemErrorId.trim();
  if (!id) return false;

  const firstTurn = buildErrorFactsTurn({
    toastTitle: diagnosis.toastTitle,
    toastBody: diagnosis.toastBody,
    route: diagnosis.route,
    systemErrorId: id,
  });

  state = {
    open: true,
    ended: false,
    submitted: false,
    offTopicRedirects: 0,
    qaPrompt,
    packet: {
      origin: 'system',
      sessionId,
      systemErrorId: id,
      diagnosis: {
        ...diagnosis,
        toastTitle: redactSecrets(diagnosis.toastTitle),
        toastBody: redactSecrets(diagnosis.toastBody),
        orgId: diagnosis.orgId,
      },
      events: [],
      transcript: [],
      repair: {},
    },
  };

  appendEvent('toast', {
    title: state.packet?.diagnosis.toastTitle,
    body: state.packet?.diagnosis.toastBody,
    httpStatus: diagnosis.httpStatus ?? null,
  });
  appendEvent('drawer_open', { systemErrorId: id, sessionId });
  for (const question of LARK_ERROR_QUESTIONS) {
    appendEvent('lark_question', { text: question });
  }
  appendTranscript('lark', firstTurn);
  emit();

  if (!qaPrompt) {
    void fetchClientErrorQaPrompt().then((prompt) => {
      if (!state.open || state.packet?.systemErrorId !== id) return;
      state = { ...state, qaPrompt: prompt };
      emit();
    });
  }

  return true;
}

export function dismissLarkErrorInterview(): void {
  if (!state.packet) {
    state = {
      open: false,
      ended: true,
      submitted: false,
      offTopicRedirects: 0,
      packet: null,
      qaPrompt: null,
    };
    emit();
    return;
  }
  appendEvent('dismiss', { systemErrorId: state.packet.systemErrorId });
  state = { ...state, open: false };
  emit();
}

/**
 * Close the sheet once the report is in. Separate from dismiss: the
 * answers were already logged by `recordLarkErrorAnswers`, so this must
 * not record an abandonment.
 */
export function closeSubmittedLarkErrorInterview(): void {
  if (!state.open) return;
  state = { ...state, open: false };
  emit();
}

export type LarkErrorAnswerResult =
  | 'submitted'
  | 'send_failed'
  | 'empty'
  | 'blocked'
  | 'redirect'
  | 'ended';

/**
 * Send the interview answers. Resolves 'submitted' only after the BE
 * accepted the Q&A event; the sheet then shows the acknowledgement
 * with its ✕. 'send_failed' keeps the form editable for a retry.
 */
export async function recordLarkErrorAnswers(
  answers: string[],
): Promise<LarkErrorAnswerResult> {
  if (!state.packet || state.ended || !state.open) return 'ended';

  const combined = answers.map((a) => a.trim());
  const nonempty = combined.filter(Boolean);
  if (nonempty.length === 0) return 'empty';

  for (const raw of nonempty) {
    const text = redactSecrets(raw);
    const decision = classifyBlockedPrompt(text);
    appendTranscript('user', text, decision.blocked
      ? { blocked: true, blockedReason: decision.reason }
      : undefined);

    if (decision.blocked && decision.reason === 'off-topic') {
      appendEvent('user_answer', { text, blocked: true, blockedReason: 'off-topic' });
      if (state.offTopicRedirects < 1) {
        state = { ...state, offTopicRedirects: 1 };
        appendEvent('off_topic_redirect', { text });
        emit();
        return 'redirect';
      }
      appendEvent('off_topic_end', { text });
      state = { ...state, ended: true, open: false };
      emit();
      return 'ended';
    }

    if (decision.blocked) {
      appendEvent('blocked_prompt', { text, reason: decision.reason });
      emit();
      return 'blocked';
    }
  }

  const transcript = LARK_ERROR_QUESTIONS.flatMap((question, index) => {
    const answer = (combined[index] ?? '').trim();
    return answer ? [{ question, answer: redactSecrets(answer) }] : [];
  });
  if (transcript.length === 0) {
    emit();
    return 'empty';
  }

  const systemErrorId = state.packet.systemErrorId;
  emit();

  const accepted = await postClientErrorQa(systemErrorId, transcript);
  if (state.packet?.systemErrorId !== systemErrorId) return 'ended';

  if (!accepted) {
    return 'send_failed';
  }

  appendTranscript('lark', 'Thanks — your answers were submitted to the team.');
  appendEvent('user_answer', { submitted: true, answerCount: transcript.length });
  state = { ...state, ended: true, submitted: true };
  emit();
  return 'submitted';
}

export function resetLarkErrorSessionForTests(): void {
  state = {
    open: false,
    ended: false,
    submitted: false,
    offTopicRedirects: 0,
    packet: null,
    qaPrompt: null,
  };
}
